import { Control, FieldError, FieldPath, FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { MaterialProperties } from '@steelbuy/ts-shared';
import { Select } from '../primitive/select/Select';

interface MaterialGenericSelectionProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> {
    material: MaterialProperties;
    name: TName;
    control: Control<TFieldValues>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    rules?: any;
    error?: FieldError;
    /** Function that returns the available options for this field given the current material */
    getOptions: (material: MaterialProperties) => Readonly<string[]>;
    /** The domainModel translation key prefix, e.g. 'material.gradeFamily.value' */
    translationKey: string;
    /** When true, the select is rendered as disabled regardless of available options */
    disabled?: boolean;
}

export const MaterialGenericSelection = <
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
    material,
    name,
    control,
    rules,
    error,
    getOptions,
    translationKey,
    disabled,
}: MaterialGenericSelectionProps<TFieldValues, TName>) => {
    const { t } = useTranslation(['uiDomain', 'domainModel']);

    let availableOptions = getOptions(material)?.slice();

    if (name !== 'quality') {
        availableOptions = availableOptions?.sort();
    }

    return (
        <Select
            options={availableOptions.map((option) => ({
                label: t(`domainModel:${translationKey}.${option}` as Parameters<typeof t>[0]),
                value: option,
            }))}
            placeholder={t('uiDomain:materialInput.placeholder')}
            name={name}
            control={control}
            rules={{ ...(rules || {}), required: availableOptions.length > 0 ? rules?.required : undefined }}
            error={error?.message}
            disabled={disabled || availableOptions.length === 0}
            searchable
        />
    );
};
